import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import type { GrantRole, LinkExpiry, ShareLink } from '../../lib/sharing-types';
import { Button, Checkbox, CopyButton, Input, Select } from '../../ui';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { shareKeys } from './keys';
import { shareUrl, withPage } from './share-url';

const ROLES: GrantRole[] = ['viewer', 'commenter', 'editor'];
const EXPIRIES: LinkExpiry[] = ['1d', '7d', '30d', 'never'];

const stateOf = (l: ShareLink, now: number): 'active' | 'expired' | 'revoked' =>
  l.revokedAt ? 'revoked' : l.expiresAt && Date.parse(l.expiresAt) <= now ? 'expired' : 'active';

/**
 * Secret links of a board (spec §9.3). The full URL exists only right after creation (only the token's hash is
 * stored); the list shows role, flags, expiry, opens and state, with a revoke that asks first.
 */
/** `page`: the page open in the editor (P14), offered as where a new link opens. */
export function LinksTab({ boardId, page }: { boardId: string; page?: { id: string; name: string } }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const links = useQuery({ queryKey: shareKeys.links(boardId), queryFn: () => api.listShareLinks(boardId) });
  const [role, setRole] = useState<GrantRole>('commenter');
  const [expires, setExpires] = useState<LinkExpiry>('7d');
  const [canExport, setCanExport] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [isProtected, setProtected] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [onPage, setOnPage] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: shareKeys.links(boardId) });

  const create = useMutation({
    mutationFn: () => api.createShareLink({ boardId, role, expires, canExport, canDownload, protected: isProtected }),
    onSuccess: ({ token }) => setCreated(withPage(shareUrl(window.location.origin, token), onPage && page ? page.id : null)),
    onError: () => setError(true),
    onSettled: refresh,
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.revokeShareLink(id), onSettled: refresh });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(false);
    setCreated(null);
    create.mutate();
  };
  const date = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);
  const now = Date.now();

  return (
    <div className="flex flex-col gap-5">
      <form className="flex flex-col gap-3" onSubmit={submit}>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
            {t('sharing.linkRole')}
            <Select value={role} onChange={(e) => setRole(e.target.value as GrantRole)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`sharing.roles.${r}`)}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
            {t('sharing.expires')}
            <Select value={expires} onChange={(e) => setExpires(e.target.value as LinkExpiry)}>
              {EXPIRIES.map((x) => (
                <option key={x} value={x}>
                  {t(`sharing.expiry.${x}`)}
                </option>
              ))}
            </Select>
          </label>
        </div>
        {role === 'editor' && <p className="font-sans text-xs text-fg-muted">{t('sharing.accountRequired')}</p>}
        <div className="flex flex-wrap gap-4">
          <Checkbox label={t('sharing.allowExport')} checked={canExport} onCheckedChange={setCanExport} />
          <Checkbox label={t('sharing.allowDownload')} checked={canDownload} onCheckedChange={setCanDownload} />
          <Checkbox label={t('sharing.protected')} checked={isProtected} onCheckedChange={setProtected} />
        </div>
        <div className="flex justify-end">
          <Button variant="primary" type="submit" disabled={create.isPending}>
            {t('sharing.createLink')}
          </Button>
        </div>
        {error && (
          <p role="alert" className="font-sans text-xs text-danger">
            {t('sharing.error')}
          </p>
        )}
      </form>
      {page && <Checkbox label={t('sharing.openOnPage', { name: page.name })} checked={onPage} onCheckedChange={setOnPage} />}

      {created && (
        <div role="status" className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input aria-label={t('sharing.newLink')} readOnly value={created} onFocus={(e) => e.target.select()} />
            <CopyButton text={created} copiedLabel={t('sharing.copied')} failedLabel={t('sharing.copyFailed')}>
              {t('sharing.copy')}
            </CopyButton>
          </div>
          <p className="font-sans text-xs text-fg-muted">{t('sharing.shownOnce')}</p>
        </div>
      )}

      <section aria-label={t('sharing.links')} className="flex flex-col gap-2">
        <h3 className="font-sans text-sm font-semibold">{t('sharing.links')}</h3>
        {links.data?.length === 0 && <p className="font-sans text-sm text-fg-muted">{t('sharing.noLinks')}</p>}
        <ul className="flex flex-col gap-2">
          {(links.data ?? []).map((l) => {
            const state = stateOf(l, now);
            const flags = [l.canExport && 'export', l.canDownload && 'download', l.protected && 'protected'].filter(Boolean) as string[];
            return (
              <li key={l.id} className="flex flex-wrap items-center gap-2 font-sans text-sm">
                <span className="min-w-0 flex-1">
                  {t(`sharing.roles.${l.role}`)} · {t(`sharing.state.${state}`)} ·{' '}
                  {l.expiresAt ? t('sharing.until', { date: date(l.expiresAt) }) : t('sharing.noExpiry')} ·{' '}
                  {t('sharing.opens', { count: l.openCount })}
                  {flags.length > 0 && <span className="text-fg-muted"> · {flags.map((f) => t(`sharing.flags.${f}`)).join(', ')}</span>}
                </span>
                {state === 'active' && <Button onClick={() => setConfirming(l.id)}>{t('sharing.revoke')}</Button>}
              </li>
            );
          })}
        </ul>
      </section>
      <ConfirmDialog
        open={confirming !== null}
        destructive
        title={t('p18.e.revokeTitle')}
        body={t('p18.e.revokeBody')}
        confirmLabel={t('sharing.confirmRevoke')}
        cancelLabel={t('p18.e.cancel')}
        onConfirm={() => {
          const id = confirming!;
          return revoke
            .mutateAsync(id)
            .catch(() => undefined)
            .finally(() => setConfirming(null));
        }}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
